import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { css } from '@emotion/react';
import { tokens } from '@hotjar/hotsauce';
import type { FormSelectedExplanationResult } from '../../types';
import type { SurveyQuestion as SurveyQuestionModel } from '../../api/survey-model';
import { S } from './StyledFormComponents';

interface DowngradeExplanationsQuestionProps {
  downgradeExplanation?: SurveyQuestionModel;
  isDisabled: boolean;
}

export function DowngradeExplanationsQuestion({
  downgradeExplanation,
  isDisabled,
}: DowngradeExplanationsQuestionProps): JSX.Element {
  const { register, control, errors, getValues, trigger } = useFormContext();
  const { t } = useTranslationWithPrefix('checkout:churn.surveyv2');
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'downgradeExplanations',
  });

  useEffect(() => {
    // clear previous fields
    remove();

    if (downgradeExplanation === undefined || downgradeExplanation.answers === undefined) {
      return;
    }

    // initializes the form fields with the explanations data
    downgradeExplanation.answers.forEach((explanations) => append(explanations));
  }, [downgradeExplanation, append, remove]);

  /**
   * Checks if the downgradeExplanations error exists, and if it's because there is no
   * explanation selected or if there are text fields empty.
   */
  const getInvalidText = (): string => {
    if ('downgradeExplanations' in errors) {
      const commentsError = errors.downgradeExplanations.some(
        (item: Record<string, { type: string; message: string }>) => item?.comment !== undefined,
      );

      return commentsError === true ? t('required-text-fields') : t('required-field');
    }

    return '';
  };

  return (
    <S.FieldList
      title={t('label-downgrade-explanation')}
      size="l"
      isInvalid={'downgradeExplanations' in errors}
      invalidText={getInvalidText()}
      isRequired
      css={css`
        & > legend {
          color: ${fields.length === 0 ? tokens.color.text_disabled : tokens.color.text_default};
          margin: ${fields.length === 0 ? 0 : `0 0 ${tokens.spacer.x2} 0`};
        }
      `}
    >
      {fields.map((item, index) => (
        <Controller
          name={`downgradeExplanations.${index}.option`}
          key={item.id}
          control={control}
          defaultValue=""
          rules={{
            required: false,
            validate: () => {
              const downgradeExplanationsValues = getValues().downgradeExplanations;

              // checks if there's at least one option selected
              return downgradeExplanationsValues.some(
                (answer: FormSelectedExplanationResult) => answer.option !== '',
              );
            },
          }}
          render={({ onChange: onChangeOption, value }) => {
            return (
              <S.Checkbox
                isChecked={value !== ''}
                isDisabled={isDisabled}
                name={`downgradeExplanations.${index}.option`}
                onChange={() => {
                  onChangeOption(value === '' ? item.text : '');

                  // trigger validation on the other checkboxes as well, otherwise the errors
                  // object will keep old errors saved
                  fields.forEach((field, i) => trigger(`downgradeExplanations.${i}.option`));
                }}
                labelContent={
                  value !== '' && item.comments === true ? (
                    <span>
                      {item.text}
                      <S.TextInput
                        name={`downgradeExplanations.${index}.comment`}
                        aria-label={`downgradeExplanations.${index}.comment`}
                        isInvalid={
                          'downgradeExplanations' in errors &&
                          errors.downgradeExplanations[index]?.comment !== undefined
                        }
                        ref={register({
                          required: t('required-field').toString(),
                        })}
                        isDisabled={isDisabled}
                      />
                    </span>
                  ) : (
                    item.text
                  )
                }
              />
            );
          }}
        />
      ))}
    </S.FieldList>
  );
}
